import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import { ESTADO_PUBLICADA } from '../../constants/publicaciones.js'
import { aplicarOrdenamiento } from '../../helpers/aplicarOrdenamiento'
import { aplicarFiltrosGenerico } from '../../helpers/filtros/aplicarFiltrosGenerico'
import { MAPA_PUBLICACIONES } from '../../helpers/filtros/mapaPublicaciones'
import { getDeepestCommonCategoryId } from '../../helpers/getDeepestCommonCategoryId.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useImagenesPublicacion } from '../../hooks/useImagenesPublicacion.js'

import { setCategoriaPath, setFiltroLocal, setOrdenamiento } from '../../slices/filterSlice.js'
import { CarritoContext } from '../context/carritoContext'
import { LoginContext } from '../context/loginContext.js'
import { usePaginacion } from '../hooks/usePaginacion'
import { Filtros } from '../reutilizables/filtros/Filtros'
import { OrdenarPor } from '../reutilizables/ordenamiento/OrdenarPor.jsx'
import { VistaCuadricula } from '../reutilizables/ordenamiento/VistaCuadricula.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion.jsx'
import * as domiciliosService from '../servicios/domiciliosService'
import { buscarTodos } from '../servicios/perfilService.js'
import { buscarPorSubCategoria, buscarTodasOptimizado } from '../servicios/publicacionesService'
import { buscarTodas, buscarImagenPorAdjunto } from '../servicios/tiendasOficialesService'
import * as usuarioService from '../servicios/usuarioService'
import Banners from './Banners'
import ComponenteError from './ComponenteError'
import Publicacion from './Publicacion'
import styles from './Publicaciones.module.css'

const ITEMS_PAGINA = 15
const ID_AVISO_SIN_PUBLICACIONES = 7
const ID_AVISO_SIN_PUBLICACIONES_TIENDA = 23

const Publicaciones = ({ ofertas }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { idSubCategoria, idVendedor } = useParams()
  const location = useLocation()
  const { idVendedorCarrito, vieneConFiltroCategoria } = location.state || {}

  const topRef = useRef(null)
  const imagenesFetchKeyRef = useRef('')
  const { loginDataContext } = useContext(LoginContext)
  const { tipoUsuario } = loginDataContext
  const { getCarrito } = useContext(CarritoContext)
  const idComprador = parseInt(localStorage.getItem('idUsuario'))
  const idVendedorFormateado = Number(idVendedor)
  const [publicacionesTotales, setPublicacionesTotales] = useState([])
  const [vistaCuadricula, setVistaCuadricula] = useState(true)
  const [bannerUrl, setBannerUrl] = useState(null)
  const [cargando, setCargando] = useState(true)
  const esOferta = ofertas === 'S'
  const empidLocalStorage = String(localStorage.getItem('empid') || '').trim()

  const {
    busca,
    ordenamiento,
    categoriaSeleccionada,
    filtrosLocales,
    filtrosSeleccionados,
    caracteristicasActuales,
    mapEtiquetaIds
  } = useSelector((state) => state.filters)

  const vendedorId = idVendedorCarrito || 0

  const [state, setState] = useState({
    tiendas: null,
    ofertas: [],
    buscar: '',
    idVendedor: 0,
    idCategoria: 0
  })
  const [perfiles, setPerfiles] = useState([])
  const [provincias, setProvincias] = useState([])
  const [localidades, setLocalidades] = useState([])
  const [proveedoresPropios, setProveedoresPropios] = useState([])

  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()

  const aplicarFiltrosPublicaciones = (arrayAFiltrar, filtros) =>
    aplicarFiltrosGenerico(arrayAFiltrar, filtros, MAPA_PUBLICACIONES)

  const publicacionesSinFiltroCaracteristicas = useMemo(() => {
    return aplicarFiltrosPublicaciones(publicacionesTotales, {
      ...filtrosLocales,
      categoriaSeleccionada,
      proveedoresPropios
    })
  }, [publicacionesTotales, filtrosLocales, categoriaSeleccionada, proveedoresPropios])

  const publicacionesFiltradas = useMemo(() => {
    return aplicarFiltrosPublicaciones(publicacionesSinFiltroCaracteristicas, {
      filtrosSeleccionados,
      caracteristicasActuales,
      mapEtiquetaIds
    })
  }, [
    publicacionesSinFiltroCaracteristicas,
    filtrosSeleccionados,
    caracteristicasActuales,
    mapEtiquetaIds
  ])

  const publicacionesAMostrar = esOferta ? publicacionesTotales : publicacionesFiltradas
  const publicacionesOrdenadas = aplicarOrdenamiento(publicacionesAMostrar, {
    orden: ordenamiento?.orden,
    campo: ordenamiento?.campo
  })

  const {
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas,
    itemsActual: publicacionesPaginadas
  } = usePaginacion(publicacionesOrdenadas, ITEMS_PAGINA)

  const sinResultados = publicacionesOrdenadas.length === 0

  const idsPublicacionesParaImagenes = useMemo(
    () => publicacionesPaginadas.map((p) => p.idPublicacion),
    [publicacionesPaginadas]
  )

  const scrollToTop = () => {
    if (typeof topRef.current?.scrollIntoView === 'function') {
      topRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const cargarDomiciliosPublicaciones = async (publicaciones) => {
    if (!publicaciones || publicaciones.length === 0) {
      setProvincias([])
      setLocalidades([])
      return
    }

    if (publicaciones[0]?.domiciliosVendedor !== undefined) {
      setPublicacionesTotales(publicaciones)
      return
    }

    try {
      const idsVendedoresUnicos = [...new Set(publicaciones.map((p) => p.idVendedor))]

      const domiciliosPorVendedor = {}
      const provinciasSet = new Set()
      const localidadesSet = new Set()

      for (const idVend of idsVendedoresUnicos) {
        try {
          const { data: domicilios } = await domiciliosService.buscarTodas(idVend, 'B')
          if (domicilios && domicilios.length > 0) {
            const domicilioPrincipal = domicilios.find(
              (d) => d.domicilioPrincipal === 1 || d.domicilioPrincipal === true
            )

            const domiciliosValidos = domicilios.filter(
              (d) => d.provincia && d.provincia.trim() && (d.localidad?.trim() || d.ciudad?.trim())
            )

            const domicilioAUsar =
              domicilioPrincipal &&
              domicilioPrincipal.provincia?.trim() &&
              (domicilioPrincipal.localidad?.trim() || domicilioPrincipal.ciudad?.trim())
                ? domicilioPrincipal
                : domiciliosValidos[0]

            if (domicilioAUsar) {
              domiciliosPorVendedor[idVend] = [domicilioAUsar]

              const provinciaNormalizada = domicilioAUsar.provincia?.toString().trim().toLowerCase()
              if (provinciaNormalizada) {
                provinciasSet.add(provinciaNormalizada)
              }
              const localidad = domicilioAUsar.localidad || domicilioAUsar.ciudad
              const localidadNormalizada = localidad?.toString().trim().toLowerCase()
              if (localidadNormalizada) {
                localidadesSet.add(localidadNormalizada)
              }
            }
          }
        } catch (error) {
          const mensaje = `Error al obtener domicilios del vendedor ${idVend}.`
          toastErrorLog(error, mensaje, 'publicaciones')
        }
      }

      const publicacionesConDomicilios = publicaciones.map((pub) => {
        const domicilios = domiciliosPorVendedor[pub.idVendedor] || []
        const primerDomicilioValido = domicilios[0] || null
        const localidadDelPrimer =
          (primerDomicilioValido?.localidad || primerDomicilioValido?.ciudad)?.toLowerCase() || ''

        return {
          ...pub,
          domiciliosVendedor: domicilios,
          domicilioVendedor: primerDomicilioValido,
          provinciaVendedor: primerDomicilioValido?.provincia?.toLowerCase() || '',
          localidadVendedor: localidadDelPrimer
        }
      })

      setPublicacionesTotales(publicacionesConDomicilios)

      setProvincias(Array.from(provinciasSet).sort())
      setLocalidades(Array.from(localidadesSet).sort())
    } catch (error) {
      const mensaje = 'Error al cargar domicilios de publicaciones.'
      toastErrorLog(error, mensaje, 'publicaciones')
    }
  }

  const showItems = useCallback(async () => {
    let publicaciones = []
    const vendedorIdValido = Number.isFinite(idVendedorFormateado)
      ? idVendedorFormateado
      : Number.isFinite(vendedorId)
        ? vendedorId
        : 0

    let requiereFiltroEstado = true

    if (vendedorIdValido) {
      const res = await buscarTodasOptimizado(idComprador, vendedorIdValido, 0, busca || '')
      const { data: todasTiendas } = await buscarTodas('')
      const tienda = todasTiendas.find((t) => t.idUsuario === vendedorIdValido) ?? null
      publicaciones = res.data.filter((p) => p.idVendedor === vendedorIdValido && p.esTiendaOficial)
      setState((prev) => ({ ...prev, tiendas: tienda }))
    } else if (idSubCategoria && idSubCategoria !== '') {
      const res = await buscarPorSubCategoria(idSubCategoria, busca || '')
      publicaciones = res.data
      requiereFiltroEstado = false
      const categoria = [...new Set(publicaciones.map((p) => p.idCategoria))]
      setState((prev) => ({ ...prev, idSubCategoria: categoria }))
    } else {
      const res = await buscarTodasOptimizado(idComprador, 0, 0, busca || '')
      publicaciones = res.data
    }

    const fechaActual = new Date()
    publicaciones = publicaciones.filter((p) => {
      if (requiereFiltroEstado && p.idPublicacionEstado !== ESTADO_PUBLICADA) {
        return false
      }

      if (p.desde && p.hasta) {
        const fechaDesde = new Date(p.desde)
        const fechaHasta = new Date(p.hasta)
        return fechaActual >= fechaDesde && fechaActual <= fechaHasta
      }

      return true
    })

    if (esOferta) {
      publicaciones = publicaciones.filter((p) => {
        if (p.precio > 0) {
          const descuento = Number(p.porcDcto) || 0
          return descuento > 0
        }
      })
    }

    if (!idSubCategoria && !vieneConFiltroCategoria) {
      const deepestCategory = getDeepestCommonCategoryId(publicaciones)
      if (deepestCategory) {
        dispatch(setCategoriaPath(deepestCategory))
      }
    }

    await cargarDomiciliosPublicaciones(publicaciones)

    setState((prev) => ({
      ...prev,
      buscar: ''
    }))
    setCargando(false)
  }, [ofertas, vendedorId, idSubCategoria, idComprador, location.pathname, busca])

  const cargarBanner = async () => {
    if (!state.tiendas?.idTiendaOficial || !state.tiendas?.idAdjuntoBanner) {
      return null
    }

    const { data } = await buscarImagenPorAdjunto(
      state.tiendas.idTiendaOficial,
      state.tiendas.idAdjuntoBanner
    )
    return URL.createObjectURL(data)
  }

  useEffect(() => {
    let blobUrl
    cargarBanner()
      .then((url) => {
        if (url) {
          blobUrl = url
          setBannerUrl(url)
        }
      })
      .catch(() => {})
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [state.tiendas?.idTiendaOficial, state.tiendas?.idAdjuntoBanner])

  const obtenerPerfiles = async () => {
    const { data } = await buscarTodos()
    const perfilesAgrupados = data
      .filter((p) => p.nombre === 'Empresa' || p.nombre === 'Particular')
      .reduce((acc, p) => {
        const existente = acc.find((x) => x.nombre === p.nombre)
        if (existente) {
          existente.idPerfiles.push(p.idPerfil)
        } else {
          acc.push({ nombre: p.nombre, idPerfiles: [p.idPerfil] })
        }
        return acc
      }, [])
    setPerfiles(perfilesAgrupados)
  }

  const obtenerProveedoresPropios = async () => {
    const { data } = await usuarioService.obtenerProveedoresPropios(empidLocalStorage)
    const proveedoresFormateados = data.map((p) => ({
      idUsuario: p.idUsuario,
      razonSocial: p.razonSocial.trim(),
      cuit: p.cuit.trim()
    }))

    setProveedoresPropios(proveedoresFormateados)
  }

  const localidadesFiltradasPorProvincia = filtrosLocales.provinciaSeleccionada
    ? [
        ...new Set(
          publicacionesTotales.flatMap((p) => {
            const domicilios = p.domiciliosVendedor || []
            return domicilios
              .filter(
                (d) =>
                  d.provincia?.toString().trim().toLowerCase() ===
                  filtrosLocales.provinciaSeleccionada
              )
              .map((d) => (d.localidad || d.ciudad)?.toString().trim().toLowerCase())
              .filter((loc) => loc)
          })
        )
      ].sort()
    : localidades

  const contextoFiltros = useMemo(
    () => ({ provincias, localidadesFiltradasPorProvincia, perfiles, proveedoresPropios }),
    [provincias, localidadesFiltradasPorProvincia, perfiles, proveedoresPropios]
  )

  useEffect(() => {
    dispatch(setFiltroLocal({ key: 'localidadSeleccionada', value: '' }))
  }, [filtrosLocales.provinciaSeleccionada])

  const onToggleClick = (_event, _publicacion, idPublicacion) => {
    navigate(`/Publicacion/${idPublicacion}`)
  }

  useEffect(() => {
    setPaginaActual(1)
    if (
      filtrosLocales.provinciaSeleccionada !== '' ||
      filtrosLocales.localidadSeleccionada !== ''
    ) {
      scrollToTop()
    }
  }, [publicacionesFiltradas])

  useEffect(() => {
    if (idsPublicacionesParaImagenes.length === 0) {
      return
    }

    const fetchKey = idsPublicacionesParaImagenes.join(',')
    if (imagenesFetchKeyRef.current === fetchKey) {
      return
    }

    imagenesFetchKeyRef.current = fetchKey
    fetchImagenes({ idPublicaciones: idsPublicacionesParaImagenes })
  }, [idsPublicacionesParaImagenes])

  // Cargar publicaciones al montar o cambiar filtros
  useEffect(() => {
    showItems()
  }, [showItems])

  useEffect(() => {
    getCarrito()
    obtenerPerfiles()
    dispatch(setOrdenamiento({ orden: 'RELEVANTE', campo: 'idPublicacion' }))
  }, [])

  useEffect(() => {
    if (tipoUsuario === 'A' && empidLocalStorage !== '') {
      obtenerProveedoresPropios()
    }
  }, [tipoUsuario, empidLocalStorage])

  return (
    <div className={styles.layout} ref={topRef}>
      <aside className={styles.filtros}>
        <Filtros
          arrayAFiltrar={publicacionesSinFiltroCaracteristicas}
          mapaFiltros={MAPA_PUBLICACIONES}
          contextoFiltros={contextoFiltros}
          filtrosVisibles={[
            'categoriaSeleccionada',
            'tipoVendedor',
            'estadoProducto',
            'precio',
            tipoUsuario === 'A' ? 'proveedorPropio' : null,
            'provinciaSeleccionada',
            'localidadSeleccionada',
            'caracteristicasActuales'
          ]}
        />
      </aside>
      <div className="col">
        {idVendedor && bannerUrl && (
          <Banners bannerPath={bannerUrl} nombreTienda={state.tiendas?.nombreTienda} />
        )}
        {idVendedor && state.tiendas?.descripcionTienda && (
          <p className={styles.descripcionTienda}>{state.tiendas.descripcionTienda}</p>
        )}
        <fieldset className={`fieldset ${styles.fieldset}`}>
          <div className={styles.headerPublicaciones}>
            <legend>Publicaciones</legend>
            <div className={styles.accionesVista}>
              <OrdenarPor
                options={[
                  {
                    label: 'Más relevantes',
                    value: { orden: 'RELEVANTE', campo: 'idPublicacion' }
                  },
                  { label: 'Menor precio', value: { orden: 'ASC', campo: 'precio' } },
                  { label: 'Mayor precio', value: { orden: 'DESC', campo: 'precio' } }
                ]}
              />
              <VistaCuadricula
                requiereCuadricula={true}
                vistaCuadricula={vistaCuadricula}
                setVistaCuadricula={setVistaCuadricula}
              />
            </div>
          </div>
          {cargando ? (
            <div className="d-flex justify-content-center p-4">
              <Spinner animation="border" role="status" aria-label="Cargando" />
            </div>
          ) : !sinResultados ? (
            <>
              <div className={vistaCuadricula ? styles.gridContainer : ''}>
                {publicacionesPaginadas.map((publicacion) => (
                  <div
                    key={publicacion.idPublicacion}
                    className={vistaCuadricula ? '' : styles.publicacion}
                  >
                    <Publicacion
                      onToggleClick={onToggleClick}
                      publicacion={publicacion}
                      vistaCuadricula={vistaCuadricula}
                      adjunto={adjuntos.find(
                        (a) => a.idPublicacion === Number(publicacion.idPublicacion)
                      )}
                      loadingImagenes={loadingImagenes}
                    />
                  </div>
                ))}
              </div>
              <Paginacion
                totalPaginas={totalPaginas}
                paginaActual={paginaActual}
                onCambioPagina={(num) => {
                  setPaginaActual(num)
                  scrollToTop()
                }}
                itemsPorPagina={itemsPorPagina}
                onCambioItemsPorPagina={(num) => {
                  setItemsPorPagina(num)
                  setPaginaActual(1)
                  scrollToTop()
                }}
              />
            </>
          ) : (
            <div className="row">
              <ComponenteError
                tipo="I"
                idAviso={
                  state.tiendas !== null
                    ? ID_AVISO_SIN_PUBLICACIONES_TIENDA
                    : ID_AVISO_SIN_PUBLICACIONES
                }
              />
            </div>
          )}
        </fieldset>
      </div>
    </div>
  )
}

Publicaciones.propTypes = {
  ofertas: PropTypes.string
}

export default Publicaciones
